import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout, { refreshAdminNotifs } from '../../components/TrackerLayout';
import { useAdminSession } from '../../context/TrackerContext';
import { getResumeReviews, markResumeReviewsSeen, resumeFileUrl, updateResumeReview } from '../../lib/api';
import type { ResumeReview, ResumeReviewFilter, ResumeReviewStatus } from '../../lib/api';

const STATUS_STYLE: Record<ResumeReviewStatus, { bg: string; color: string }> = {
  'Booked':    { bg: '#fef3c7', color: '#92400e' },
  'Done':      { bg: '#dcfce7', color: '#166534' },
  'No Show':   { bg: '#fee2e2', color: '#991b1b' },
  'Cancelled': { bg: '#f3f4f6', color: '#6b7280' },
};

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

// ── Main Page ─────────────────────────────────────────────────────────────────
// Resume review slots booked on the public /resume-review page: 30 minutes
// each, 3–6 PM, one person per slot. Opening this page clears new bookings
// from the bell. Cancelling frees the slot for someone else.

const ResumeReviews: React.FC = () => {
  const { adminPassword } = useAdminSession();
  const [filter, setFilter] = useState<ResumeReviewFilter>('Upcoming');
  const [reviews, setReviews] = useState<ResumeReview[]>([]);
  const [counts, setCounts] = useState({ upcoming: 0, today: 0, unseen: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  // Bookings that were new when the page opened keep their NEW tag until it's left
  const [fresh, setFresh] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getResumeReviews(adminPassword, filter);
      setReviews(res.reviews ?? []);
      setCounts(res.counts);
      const unseen = (res.reviews ?? []).filter(r => !r.seen).map(r => r._id);
      if (unseen.length) setFresh(f => new Set([...Array.from(f), ...unseen]));
      if (res.counts.unseen > 0) {
        await markResumeReviewsSeen(adminPassword);
        refreshAdminNotifs();
      }
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword, filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (r: ResumeReview, status: ResumeReviewStatus) => {
    if (status === 'Cancelled' && !window.confirm(`Cancel ${r.name}'s review on ${r.dateLabel}, ${r.timeLabel}? The slot opens up for someone else. ${r.name.split(' ')[0]} is not emailed, so let them know.`)) return;
    setBusy(r._id);
    try {
      await updateResumeReview(adminPassword, r._id, { status });
      toast.success(status === 'Booked' ? `${r.name} is booked again` : `${r.name}: ${status}`);
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  const saveNote = async (r: ResumeReview) => {
    const note = (notes[r._id] ?? r.note).trim();
    if (note === r.note) return;
    try {
      await updateResumeReview(adminPassword, r._id, { note });
      setReviews(list => list.map(x => (x._id === r._id ? { ...x, note } : x)));
      toast.success('Note saved');
    } catch (err: any) { toast.error(err.message); }
  };

  const formLink = `${window.location.origin}/resume-review`;
  const copyLink = () => {
    navigator.clipboard?.writeText(formLink).then(() => toast.success('Form link copied'), () => toast.info(formLink));
  };

  // Grouped by day, in the order the server sent them
  const groups: { date: string; label: string; items: ResumeReview[] }[] = [];
  for (const r of reviews) {
    const last = groups[groups.length - 1];
    if (last && last.date === r.date) last.items.push(r);
    else groups.push({ date: r.date, label: r.dateLabel, items: [r] });
  }

  const filterCount = (f: ResumeReviewFilter) => (f === 'Upcoming' ? counts.upcoming : f === 'Today' ? counts.today : 0);

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">📄</span>
                <h1 className="text-xl font-bold text-white">Resume Reviews</h1>
              </div>
              <p className="text-white/50 text-xs">Booked from the website form: 30-minute slots, 3–6 PM, Mon–Sat. Each new booking is emailed to you with the resume and sent to the app.</p>
            </div>
            <div className="flex gap-2 self-start sm:self-auto">
              <button onClick={copyLink}
                className="px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
                🔗 Copy form link
              </button>
              <button onClick={load}
                className="px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
                ↻ Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {(['Upcoming', 'Today', 'Past', 'All'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className="text-xs font-semibold px-4 py-2 rounded-xl border-2 transition-colors"
              style={filter === f
                ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
              {f}{filterCount(f) > 0 ? ` (${filterCount(f)})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading bookings…</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-14 text-center">
            <div className="text-5xl mb-3">🗓️</div>
            <p className="text-gray-700 font-bold text-base">
              {filter === 'Upcoming' ? 'No upcoming resume reviews' : filter === 'Today' ? 'No resume reviews today' : 'Nothing here yet'}
            </p>
            <p className="text-gray-400 text-xs mt-1 break-all">Share the form: {formLink}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map(g => (
              <div key={g.date} className="space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  {g.label} <span className="text-gray-400 font-semibold normal-case">· {g.items.length} booking{g.items.length === 1 ? '' : 's'}</span>
                </p>
                {g.items.map(r => {
                  const st = STATUS_STYLE[r.status];
                  const isBooked = r.status === 'Booked';
                  return (
                    <div key={r._id} data-review={r._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                      style={isBooked && !r.over ? { borderLeftWidth: 4, borderLeftColor: '#F0B429' } : {}}>
                      <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
                        <div className="sm:w-36 shrink-0 rounded-xl px-3 py-2 text-center self-start"
                          style={{ background: isBooked && !r.over ? '#fffbeb' : '#f9fafb' }}>
                          <p className="text-sm font-extrabold text-purple-900 whitespace-nowrap">{r.timeLabel}</p>
                          <p className="text-[11px] text-gray-500">{r.dateLabel}</p>
                        </div>

                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-gray-800">{r.name}</p>
                                {fresh.has(r._id) && <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded text-white" style={{ background: '#FF6535' }}>NEW</span>}
                              </div>
                              <p className="text-sm text-gray-600 mt-0.5 break-all">
                                <a href={`tel:+91${r.phone}`} className="text-purple-700 font-semibold">{r.phone}</a>
                                {' · '}
                                <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a>
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                Booked {fmtWhen(r.createdAt)}
                                {r.statusUpdatedAt && !isBooked ? ` · marked ${r.status} ${fmtWhen(r.statusUpdatedAt)}` : ''}
                              </p>
                            </div>
                            <span className="text-xs font-bold px-2.5 py-1 rounded-lg" style={{ background: st.bg, color: st.color }}>
                              {isBooked && r.over ? 'Booked · time over' : r.status}
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-2 items-center">
                            {r.resumeUrl ? (
                              <a href={resumeFileUrl(r.resumeUrl)} target="_blank" rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 border-purple-200 text-purple-800 hover:bg-purple-50"
                                title={r.resumeFileName}>
                                📄 Open resume
                              </a>
                            ) : (
                              <span className="text-xs text-gray-400">No resume file</span>
                            )}
                            {isBooked ? (
                              <>
                                <button onClick={() => setStatus(r, 'Done')} disabled={busy === r._id}
                                  className="px-3 py-1.5 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={{ background: '#16a34a' }}>
                                  ✓ Done
                                </button>
                                <button onClick={() => setStatus(r, 'No Show')} disabled={busy === r._id}
                                  className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 disabled:opacity-50" style={{ borderColor: '#fca5a5', color: '#b91c1c' }}>
                                  No show
                                </button>
                                <button onClick={() => setStatus(r, 'Cancelled')} disabled={busy === r._id}
                                  className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 border-gray-200 text-gray-500 disabled:opacity-50">
                                  ✕ Cancel
                                </button>
                              </>
                            ) : (
                              <button onClick={() => setStatus(r, 'Booked')} disabled={busy === r._id}
                                className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 border-gray-200 text-gray-500 disabled:opacity-50">
                                ↩ Back to Booked
                              </button>
                            )}
                          </div>

                          <input value={notes[r._id] ?? r.note} onChange={e => setNotes(n => ({ ...n, [r._id]: e.target.value }))}
                            onBlur={() => saveNote(r)} onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            placeholder="Add a note (saved when you click away)"
                            className="w-full px-3 py-2 border-2 border-gray-100 rounded-xl text-sm focus:outline-none focus:border-purple-300" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default ResumeReviews;
